import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import { Theme, createTheme } from "@mui/material/styles";

/**
 * Utility to simplify root style overrides for MUI components.
 * @param styles - A function that receives the theme and returns styles.
 * @returns An object formatted for MUI component overrides.
 */
const rootOverride = (styles: (theme: Theme) => object): any => ({
  styleOverrides: { root: ({ theme }: { theme: Theme }) => styles(theme) },
});

const PALETTE = {
  common: {
    black: "#1A0245",
    white: "#FFFFFF",
    grey: "#AB99EE"
  },
  primary: {
    main: "#1147EA", // Blue - primary CTA and links
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#2D1583", // Dark 1 - headlines on light backgrounds
    contrastText: "#FFFFFF",
    light: "#AB99EE" // Light Purple - alternative for text
  },
  background: {
    default: "#F4F9FF", // Light 3 - primary background fill
    light: "#CADAF4", // Light 1 - alternative for text
    paper: "#FFFFFF", // White - alternative background fill
    dark: "#1A0245", // Black - dark background fill
  },
  text: {
    primary: "#2D1583", // Black - body copy
    secondary: "#1A0245", // Dark 1 - alternative for text
    light: "#CADAF4", // Light 1 - alternative for text
  },
  action: {
    active: "#1147EA", // Blue - primary clickable elements
    hover: "#CADAF4", // Light 1 - background fills
    selected: "#E3EFFF", // Light 2 - alternative background
    disabled: "rgba(0, 0, 0, 0.26)"
  },
  warning: {
    main: "#D68B18", // Gold - small UI elements and embellishments
  },
  success: {
    main: "#AB99EE", // Light Purple - primary CTA and links
    contrastText: "#FFFFFF",
  },
  divider: "#CADAF4", // Light 1 used for subtle dividers
};

const theme = createTheme({
  palette: PALETTE,
  spacing: 16,
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: "Poppins, sans-serif",
    // h1: {
    //   fontSize: 104,
    //   fontWeight: 'bold',
    //   lineHeight: 1.1,
    // },
    // h2: {
    //   fontSize: 64,
    //   fontWeight: 'semi-bold',
    //   lineHeight: 1.3,
    // },
    // h3: {
    //   fontSize: 48,
    //   fontWeight: 'semi-bold',
    //   lineHeight: 1.3,
    // },
    // h4: {
    //   fontSize: 40,
    //   fontWeight: 'semi-bold',
    //   lineHeight: 1.3,
    // },
    // h5: {
    //   fontSize: 32,
    //   fontWeight: 'semi-bold',
    //   lineHeight: 1.3,
    // },
    // h6: {
    //   fontSize: 28,
    //   fontWeight: 'semi-bold',
    //   lineHeight: 1.3,
    // },
    // body1: {
    //   fontSize: 16,
    //   lineHeight: 1.6,
    // },
    // body2: {
    //   fontSize: 14,
    //   lineHeight: 1.4,
    // },
  },
  components: {
    MuiButton: {
      ...rootOverride((theme) => ({
        borderRadius: 50,
        boxShadow: "none",
        textTransform: "initial",
      })),
    },
    MuiTextField: {
      ...rootOverride((theme) => ({
        margin: "18px 0",
        "& fieldset": {
          borderRadius: 8,
        },
      })),
    },
    MuiFormControl: {
      ...rootOverride((theme) => ({
        borderRadius: 8,
      })),
    },
    MuiSvgIcon: {
      ...rootOverride((theme) => ({
        color: theme.palette.secondary.main,
      })),
    },
    MuiDialogTitle: {
      ...rootOverride((theme) => ({
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.3,
      })),
    },
    // CONFIG BELOW IS NOT NEEDED - CAN BE USED AS REFERENCE FOR NOW
    // MuiOutlinedInput: {
    //   ...rootOverride({
    //     fontSize: "14px",
    //   }),
    // },
    // MuiInputLabel: {
    //   ...rootOverride({
    //     fontSize: "14px",
    //   }),
    // },
  },
  // components: {
  //   MuiButton: {
  //     ...rootOverride({
  //       textTransform: 'none',
  //       borderRadius: '30px',
  //       boxShadow: 'none',
  //       '&:disabled': {
  //         backgroundColor: `${COLORS.LIGHT_GREY} !important`,
  //       },
  //       '&.MuiButton-contained': {
  //         backgroundColor: COLORS.LIGHT_BLUE,
  //         fontFamily: 'Poppins !important',
  //         '&:hover': {
  //           boxShadow: 'none !important',
  //           backgroundColor: COLORS.LIGHT_BLUE_HOVER,
  //         },
  //         '&:active': {
  //           boxShadow: 'none !important',
  //         },
  //       },
  //       '&.MuiButton-outlined': {
  //         borderColor: COLORS.LIGHT_BLUE,
  //         color: COLORS.LIGHT_BLUE,
  //         '&:hover': {
  //           boxShadow: 'none !important',
  //           borderColor: COLORS.LIGHT_BLUE_HOVER,
  //           color: COLORS.LIGHT_BLUE_HOVER,
  //         },
  //         '&:active': {
  //           boxShadow: 'none !important',
  //         },
  //       },
  //       '&.MuiButton-text': {
  //         color: COLORS.LIGHT_BLUE,
  //         '&:hover': {
  //           color: COLORS.LIGHT_BLUE_HOVER,
  //         },
  //       },
  //       '&.large-btn': {
  //         fontSize: '24px !important',
  //         fontWeight: '700 !important',
  //         padding: '4px 30px !important',
  //       },
  //       '&.wide-btn': {
  //         padding: '5px 30px !important',
  //       },
  //     }),
  //   },
  //   MuiTextField: {
  //     ...rootOverride({
  //       '& .MuiOutlinedInput-root': {
  //         borderRadius: '10px',
  //         fontFamily: 'Poppins, sans-serif',
  //         '& .MuiOutlinedInput-notchedOutline': {
  //           borderRadius: '10px',
  //         },
  //       },
  //       '& .MuiInputLabel-root': {
  //         fontFamily: 'Poppins, sans-serif',
  //       },
  //     }),
  //   },
  //   MuiAutocomplete: {
  //     ...rootOverride({
  //       '& .MuiOutlinedInput-root': {
  //         borderRadius: '10px',
  //         fontFamily: 'Poppins, sans-serif',
  //       },
  //       '& .MuiAutocomplete-input': {
  //         fontFamily: 'Poppins, sans-serif',
  //       },
  //     }),
  //   },
  //   MuiCard: {
  //     ...rootOverride({
  //       borderRadius: '30px',
  //     }),
  //   },
  // },
});

export default theme;
